import { StyleSheet, Text, View } from 'react-native';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { TouchableOpacity } from 'react-native';
import { STYLE_VARS } from '../../../constants/style-vars';
import { Image } from '../../../components/Image';
import dayjs from 'dayjs';
import { BookingType } from '../types';
import { Button } from '../../../components/Button';
import { COLORS, FONTS } from '../../../constants';
import DatetimeInfo from '../../BookingDetail/components/DatetimeInfo';
import ProductTable from '../../BookingDetail/components/ProductTable';
import { useIsDesktop } from '../../../hooks/useIsDeskTop';
import { IBooking } from '../../../types/bookings.types';
import { useSelector } from 'react-redux';
import { RootState } from '../../../services/redux';
import { convertToFullPhone } from '../../../services/utils/phoneConvert';
import { convertPrice } from '../../../services/utils';
import { BOOKING_STATUS } from '../../../constants/camp';
import { getDatesBetween } from '../../../services/utils/dateConvert';

interface BookingCollapseProps {
  bookingDetail: IBooking;
}

interface ITableItem {
  product: string;
  desc: string;
  price: string;
}

const BookingCollapse: FC<BookingCollapseProps> = ({ bookingDetail }) => {
  const betweenDates = getDatesBetween(
    dayjs(new Date(bookingDetail.check_in)).format('DD/MM/YYYY'),
    dayjs(new Date(bookingDetail.check_out)).format('DD/MM/YYYY'),
    bookingDetail.bookable.room_availability
  );
  const { user } = useSelector((redux: RootState) => redux.authReducer);
  const [open, setOpen] = useState<boolean>(false);
  const isDesktop = useIsDesktop();

  const showPrice = (price: string | number) => {
    if (price) {
      return convertPrice(Number(price));
    } else {
      return 'Free';
    }
  };

  const showStatus = (status: number) => {
    var text = '';
    var color = '';

    switch (status) {
      case BOOKING_STATUS.PENDING:
        text = 'PENDING';
        color = COLORS.LIGHT_GREEN;
        break;
      case BOOKING_STATUS.PAID:
        text = 'PAID';
        color = COLORS.LIGHT_GREEN;
        break;
      case BOOKING_STATUS.FAILED:
        text = 'FAILED';
        color = COLORS.RED_PRIMARY;
        break;
      case BOOKING_STATUS.COMPLETED:
        text = 'COMPLETED';
        color = COLORS.LIGHT_GREEN;
        break;
      case BOOKING_STATUS.INCOMPLETE:
        text = 'INCOMPLETE';
        color = COLORS.RED_PRIMARY;
        break;
    }

    return (
      <Text
        style={[
          styles.contentText,
          { textTransform: 'uppercase', color: color },
        ]}
      >
        {text}
      </Text>
    );
  };

  const renderTableItemRow = useCallback((item: ITableItem) => {
    return (
      <View style={{ flexDirection: 'row', marginBottom: 15 }}>
        <View style={{ width: '75%' }}>
          <Text style={styles.tableContentText}>{item.product}</Text>
          <Text style={[styles.tableContentText, { fontSize: 16 }]}>
            {item.desc}
          </Text>
        </View>
        <View style={{ width: '25%' }}>
          <Text style={styles.tableContentText}>{item.price}</Text>
        </View>
      </View>
    );
  }, []);

  const renderBottomInfor = useMemo(() => {
    return (
      <View
        style={[
          {
            backgroundColor: '#f9f2f1',
            borderRadius: 18,
          },
          isDesktop()
            ? {
                flexDirection: 'row',
                padding: 20,
                paddingBottom: 10,
                flexWrap: 'wrap',
                gap: 15,
              }
            : {
                flexDirection: 'column',
                gap: 20,
                padding: 16,
              },
        ]}
      >
        <View
          style={[
            isDesktop() && {
              flex: 0.333333,
              minWidth: 200,
            },
          ]}
        >
          <Text style={styles.headerText}>Emergency Contact</Text>
          <Text style={styles.contentInfoText}>{user?.email}</Text>
        </View>
        <View
          style={[
            isDesktop() && {
              flex: 0.333333,
              minWidth: 200,
            },
          ]}
        >
          <Text style={styles.headerText}>Contact Number:</Text>
          <Text style={styles.contentInfoText}>
            {convertToFullPhone(user?.phone_country, user?.phone)}
          </Text>
        </View>
        <View
          style={[
            isDesktop() && {
              flex: 0.333333,
              minWidth: 200,
            },
          ]}
        >
          <Text style={styles.headerText}>Time of Payment</Text>
          <Text style={styles.contentInfoText}>
            {dayjs(new Date(bookingDetail.created_at)).format('hh:mm A')}
          </Text>
        </View>
        <View
          style={[
            isDesktop() && {
              flex: 0.333333,
              minWidth: 200,
            },
          ]}
        >
          <Text style={styles.headerText}>Date of Payment</Text>
          <Text style={styles.contentInfoText}>
            {dayjs(new Date(bookingDetail.created_at)).format('DD/MM/YYYY')}
          </Text>
        </View>
        <View
          style={[
            isDesktop() && {
              flex: 0.333333,
              minWidth: 200,
            },
          ]}
        >
          <Text style={styles.headerText}>Amount Paid</Text>
          <Text style={styles.contentInfoText}>{`$${
            bookingDetail.total_in_cents / 100
          } HKD`}</Text>
        </View>
        <View
          style={[
            isDesktop() && {
              flex: 0.333333,
              minWidth: 200,
            },
          ]}
        >
          <Text style={styles.headerText}>Booking ID</Text>
          <Text style={styles.contentInfoText}>{bookingDetail.id}</Text>
        </View>
        <View
          style={[
            isDesktop() && {
              flex: 0.333333,
              minWidth: 200,
            },
          ]}
        >
          <Text style={styles.headerText}>Payment Method</Text>
          <Text style={styles.contentInfoText}>
            {bookingDetail.payment_method.name}
          </Text>
        </View>
      </View>
    );
  }, [isDesktop, bookingDetail, user]);

  const renderDatetimeInfor = useMemo(() => {
    return (
      <View
        style={[
          {
            backgroundColor: '#f9f2f1',
            borderRadius: 18,
            paddingTop: 20,
            paddingBottom: 20,
          },
          isDesktop()
            ? {
                flexDirection: 'row',
                paddingHorizontal: 25,
              }
            : {
                flexDirection: 'column',
                gap: 25,
                paddingHorizontal: 20,
              },
        ]}
      >
        <View
          style={[
            {
              flex: 0.333333,
            },
          ]}
        >
          <Text
            style={{
              fontFamily: FONTS.AvenirMedium,
              fontWeight: '800',
              fontSize: 19,
              letterSpacing: 1,
              marginBottom: 15,
              color: COLORS.BLACK,
            }}
          >
            Check in:
          </Text>
          <Text
            style={{
              fontFamily: FONTS.AvenirMedium,
              fontWeight: '500',
              fontSize: 16,
              color: COLORS.GREY,
              marginBottom: 13,
            }}
          >
            {dayjs(new Date(bookingDetail.check_in)).format('ddd DD MMMM YYYY')}
          </Text>
          <View
            style={{
              flexDirection: 'row',
            }}
          >
            <View
              style={{
                paddingHorizontal: 13,
                paddingVertical: 8,
                borderRadius: 18,
                backgroundColor: COLORS.LIGHT_GREY,
              }}
            >
              <Text
                style={{
                  fontFamily: FONTS.AvenirMedium,
                  fontSize: 12,
                  fontWeight: '500',
                  color: COLORS.BLACK,
                }}
              >
                {dayjs(new Date(bookingDetail.check_in)).format('hh:mm a')}
              </Text>
            </View>
          </View>
        </View>
        <View
          style={[
            {
              flex: 0.333333,
            },
          ]}
        >
          <Text
            style={{
              fontFamily: FONTS.AvenirMedium,
              fontWeight: '800',
              fontSize: 19,
              letterSpacing: 1,
              marginBottom: 15,
              color: COLORS.BLACK,
            }}
          >
            Check out:
          </Text>
          <Text
            style={{
              fontFamily: FONTS.AvenirMedium,
              fontWeight: '500',
              fontSize: 16,
              color: COLORS.GREY,
              marginBottom: 13,
            }}
          >
            {dayjs(new Date(bookingDetail.check_out)).format(
              'ddd DD MMMM YYYY'
            )}
          </Text>
          <View
            style={{
              flexDirection: 'row',
            }}
          >
            <View
              style={{
                paddingHorizontal: 13,
                paddingVertical: 8,
                borderRadius: 18,
                backgroundColor: COLORS.LIGHT_GREY,
              }}
            >
              <Text
                style={{
                  fontFamily: FONTS.AvenirMedium,
                  fontSize: 12,
                  fontWeight: '500',
                  color: COLORS.BLACK,
                }}
              >
                {dayjs(new Date(bookingDetail.check_out)).format('hh:mm a')}
              </Text>
            </View>
          </View>
        </View>
        <View
          style={[
            {
              flex: 0.333333,
            },
          ]}
        >
          <Text
            style={{
              fontFamily: FONTS.AvenirMedium,
              fontWeight: '800',
              fontSize: 19,
              letterSpacing: 1,
              marginBottom: 15,
              color: COLORS.BLACK,
            }}
          >
            Number of People:
          </Text>
          <Text
            style={{
              fontFamily: FONTS.AvenirMedium,
              fontWeight: '500',
              fontSize: 16,
              color: COLORS.GREY,
              marginBottom: 13,
            }}
          >
            {`${
              Number(bookingDetail.adult) + Number(bookingDetail.elderly)
            } people`}
          </Text>
        </View>
      </View>
    );
  }, [isDesktop, bookingDetail]);

  const renderProductTable = useMemo(() => {
    return (
      <View style={{ paddingHorizontal: 20 }}>
        <View
          style={{
            flexDirection: 'row',
            paddingBottom: 10,
            borderBottomColor: COLORS.LIGHT_LIGHT_GREY,
            borderBottomWidth: 1,
            marginBottom: 8,
          }}
        >
          <View style={{ width: '75%' }}>
            <Text style={styles.tableHeaderText}>Product</Text>
          </View>
          <View
            style={[
              {
                width: '25%',
              },
              isDesktop()
                ? {
                    flexDirection: 'row',
                    alignItems: 'center',
                  }
                : {
                    flexDirection: 'column',
                  },
            ]}
          >
            <Text style={styles.tableHeaderText}>Price</Text>
            <Text
              style={{
                fontFamily: FONTS.AvenirMedium,
                fontSize: 12,
                fontWeight: '500',
                marginLeft: 3,
                color: COLORS.GREY,
              }}
            >
              {'(HKD)'}
            </Text>
          </View>
        </View>
        <View
          style={{
            borderBottomColor: COLORS.LIGHT_LIGHT_GREY,
            borderBottomWidth: 1,
            marginBottom: 10,
          }}
        >
          <View style={{ marginBottom: 10 }}>
            <View style={{ marginBottom: 8 }}>
              <Text style={styles.tableHeaderText}>Camp Type</Text>
            </View>
            {renderTableItemRow({
              product: bookingDetail.bookable.name.en,
              desc: '',
              price: showPrice(
                bookingDetail.camp_price > 0
                  ? bookingDetail.camp_price / 100
                  : 0
              ),
            })}
          </View>
          {bookingDetail.rooms.length > 0 && (
            <View style={{ marginBottom: 10 }}>
              <View style={{ marginBottom: 8 }}>
                <Text style={styles.tableHeaderText}>Rooms</Text>
              </View>
              <View>
                {bookingDetail.rooms.map((room) => {
                  return (
                    <>
                      {renderTableItemRow({
                        product: room.name,
                        desc: `No. Of Room: ${room.quantity}`,
                        price: `${showPrice(room.total / 100)}`,
                      })}
                    </>
                  );
                })}
              </View>
            </View>
          )}
          <View style={{ marginBottom: 10 }}>
            <View style={{ marginBottom: 8 }}>
              <Text style={styles.tableHeaderText}>Meals</Text>
            </View>

            {bookingDetail.dishes && (
              <View>
                {bookingDetail.dishes.map((item) => {
                  return (
                    <>
                      {renderTableItemRow({
                        product: item.name,
                        desc: `Amount: ${item.quantity}`,
                        price: showPrice(item.total / 100),
                      })}
                    </>
                  );
                })}
              </View>
            )}
          </View>
          <View style={{ flexDirection: 'row', marginBottom: 20 }}>
            <View style={{ width: '30%' }}>
              <Text style={[styles.tableContentText, { color: COLORS.BLACK }]}>
                Total Price:
              </Text>
            </View>
            <View style={{ width: '45%' }}>
              <Text style={[styles.tableContentText, { color: COLORS.BLACK }]}>
                {'Subsidised booking rate applied'}
              </Text>
            </View>
            <View style={{ width: '25%' }}>
              <Text style={[styles.tableContentText, { color: COLORS.BLACK }]}>
                {showPrice(bookingDetail.total_in_cents / 100)}
              </Text>
            </View>
          </View>
        </View>
      </View>
    );
  }, [bookingDetail, isDesktop, renderTableItemRow]);

  return (
    <View>
      <View
        style={[
          {
            flexDirection: 'column',
            backgroundColor: COLORS.LIGHT_RED,
            borderRadius: 18,
            paddingTop: 15,
            marginBottom: open && isDesktop() ? 25 : 0,
          },
          isDesktop()
            ? {}
            : {
                paddingHorizontal: 5,
              },
        ]}
      >
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'center',
            paddingBottom: 15,
          }}
        >
          <TouchableOpacity
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: COLORS.RED_PRIMARY,
              paddingHorizontal: 14,
              paddingVertical: 5,
              borderRadius: 13,
            }}
            onPress={() => {
              setOpen(!open);
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.AvenirMedium,
                fontWeight: '500',
                fontSize: 16,
                color: COLORS.WHITE,
                marginRight: 5,
              }}
            >
              {open ? 'Collapse' : 'Detail'}
            </Text>
            <Image
              source={
                open
                  ? require('../../../assets/up-arrow.png')
                  : require('../../../assets/down-arrow.png')
              }
              style={{
                width: 18,
                height: 10,
                tintColor: COLORS.WHITE,
              }}
            />
          </TouchableOpacity>
        </View>
        <View
          style={{
            flexDirection: 'row',
            marginBottom: 15,
          }}
        >
          <View
            style={{
              width: '25%',
            }}
          >
            <Text style={styles.contentText}>
              {dayjs(new Date(bookingDetail.created_at)).format('DD MMM YYYY')}
            </Text>
          </View>
          <View
            style={{
              width: '22%',
            }}
          >
            <Text style={styles.contentText}>{bookingDetail.id}</Text>
          </View>
          <View
            style={{
              width: '25%',
            }}
          >
            <Text style={styles.contentText}>
              {`$${bookingDetail.total_in_cents / 100} HKD`}
            </Text>
          </View>
          <View
            style={{
              width: '28%',
            }}
          >
            {showStatus(bookingDetail.status)}
          </View>
        </View>
        {open && (
          <View
            style={{
              paddingHorizontal: isDesktop() ? 20 : 10,
            }}
          >
            {/* <View
              style={{
                flexDirection: 'column',
                backgroundColor: COLORS.WHITE,
                borderRadius: 18,
                borderWidth: 1,
                borderColor: '#DDDDE3',
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  paddingTop: 20,
                  justifyContent: 'space-between',
                  borderBottomWidth: 1,
                  borderBottomColor: COLORS.LGREY,
                }}
              >
                <View
                  style={{
                    width: '30%',
                  }}
                >
                  <Text
                    style={[styles.innerTableHeader, { textAlign: 'right' }]}
                  >
                    Camp
                  </Text>
                </View>
                <View
                  style={{
                    width: '30%',
                  }}
                >
                  <Text
                    style={[styles.innerTableHeader, { textAlign: 'center' }]}
                  >
                    No. Of Guests
                  </Text>
                </View>
                <View
                  style={{
                    width: '30%',
                    flexDirection: 'row',
                  }}
                >
                  <View
                    style={{
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={[styles.innerTableHeader]}>Price</Text>
                    <span
                      style={{
                        fontFamily: FONTS.AvenirMedium,
                        fontWeight: '500',
                        color: COLORS.BLACK,
                        fontSize: 10,
                      }}
                    >
                      {'(HKD)'}
                    </span>
                  </View>
                </View>
              </View>
              <View
                style={[
                  {
                    flexDirection: 'column',
                  },
                  isDesktop()
                    ? {
                        paddingVertical: 20,
                        paddingHorizontal: 27,
                        marginBottom: 20,
                      }
                    : {
                        paddingVertical: 15,
                        paddingHorizontal: 15,
                      },
                ]}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <View
                    style={{
                      width: '30%',
                    }}
                  >
                    <View
                      style={{ flexDirection: 'column', gap: 10, width: 110 }}
                    >
                      <Image
                        style={{
                          width: 100,
                          height: 100,
                          borderRadius: 18,
                        }}
                        source={require('../../../assets/activities_image/image-1.png')}
                      />
                      <Text
                        style={[styles.innerTableText, { textAlign: 'center' }]}
                      >
                        OVERNIGHT CAMP
                      </Text>
                    </View>
                  </View>
                  <View
                    style={{
                      width: '30%',
                    }}
                  >
                    <Text
                      style={[styles.innerTableText, { textAlign: 'center' }]}
                    >
                      3
                    </Text>
                  </View>
                  <View
                    style={{
                      width: '30%',
                    }}
                  >
                    <Text
                      style={[styles.innerTableText, { textAlign: 'center' }]}
                    >
                      $580
                    </Text>
                  </View>
                </View>
              </View>
            </View> */}
            {renderDatetimeInfor}
            {open && !isDesktop() && (
              <>
                {renderProductTable}
                {renderBottomInfor}
              </>
            )}
          </View>
        )}
      </View>
      {open && isDesktop() && (
        <>
          {renderProductTable}
          {renderBottomInfor}
        </>
      )}
    </View>
  );
};

export default BookingCollapse;

const styles = StyleSheet.create({
  contentText: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '500',
    fontSize: 16,
    color: COLORS.BLACK,
    textAlign: 'center',
  },
  innerTableHeader: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '500',
    color: COLORS.BLACK,
    fontSize: 18,
  },
  innerTableText: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '500',
    color: COLORS.BLACK,
    fontSize: 16,
  },
  headerText: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '800',
    fontSize: 19,
    letterSpacing: 1,
    marginBottom: 15,
    color: COLORS.BLACK,
  },
  contentInfoText: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '500',
    fontSize: 16,
    color: COLORS.GREY,
  },
  tableHeaderText: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '800',
    fontSize: 19,
    color: COLORS.BLACK,
  },
  tableContentText: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '500',
    fontSize: 17,
    color: COLORS.GREY,
    paddingRight: 5,
  },
});
